import { camelCase, findIndex } from "lodash";
import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { useDelayedAction } from "@krea/customer-features/hooks";
import FieldRequestExplanation from "@krea/customer-features/shared-components/field-request/FieldRequestExplanation";
import { FieldRequestNavigator } from "@krea/customer-features/shared-components/field-request/FieldRequestNavigator";
import { Text } from "@krea/customer-features/shared-components/text";
import { useIsFieldRequestSubmitting } from "@krea/customer-features/store/field-requests/hooks/mutations";
import {
  useGetFieldRequests,
  useGetOneFieldRequest,
} from "@krea/customer-features/store/field-requests/hooks/queries";
import { IFieldRequest } from "@krea/customer-features/types/fieldRequest";
import { getFilteredFieldRequests } from "@krea/customer-features/utils";
import { twMerge } from "@krea/shared/design-system/tailwind-utils";

import { useGetCurrentLoanApplication } from "store/current-loan-application/hooks/queries";

const getNextRequest = (
  triageFields: string[],
  fieldRequests: IFieldRequest[],
  field: string,
) => {
  const triageFieldsResult = triageFields.length
    ? getFilteredFieldRequests(triageFields, fieldRequests)
    : fieldRequests;

  const stepIndex = findIndex(
    triageFieldsResult,
    (request) => camelCase(request.field) === field,
  );
  const nextStep =
    stepIndex !== triageFieldsResult.length - 1 ? stepIndex + 1 : null;

  return nextStep ? triageFieldsResult[nextStep] : undefined;
};

const getPreviousRequest = (
  triageFields: string[],
  fieldRequests: IFieldRequest[],
  field: string,
) => {
  const triageFieldsResult = triageFields.length
    ? getFilteredFieldRequests(triageFields, fieldRequests)
    : fieldRequests;

  const stepIndex = findIndex(
    triageFieldsResult,
    (request) => camelCase(request.field) === field,
  );
  const previousStep = stepIndex !== 0 ? stepIndex - 1 : null;

  return previousStep != null ? triageFieldsResult[previousStep] : undefined;
};

export const FieldRequestStepNavigation = ({
  field,
  children,
  preview,
  title,
  fieldRequestID,
}: {
  field: string;
  children: React.ReactNode;
  preview: boolean;
  title: string;
  fieldRequestID: string;
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const isFieldRequestSubmitting = useIsFieldRequestSubmitting(fieldRequestID);
  const delayAction = useDelayedAction(isFieldRequestSubmitting);

  const { data } = useGetCurrentLoanApplication();
  const triageFields = data?.triageFields ?? [];
  const { data: fieldRequests = [] } = useGetFieldRequests();
  const { data: currentFieldRequest } = useGetOneFieldRequest(fieldRequestID);

  const isFieldRequestSubmitted = currentFieldRequest?.isSubmitted ?? false;
  const fieldRequestDescription = currentFieldRequest?.description ?? "";
  const fieldRequestSubtitle = currentFieldRequest?.subtitle ?? "";

  const nextFieldRequest = getNextRequest(triageFields, fieldRequests, field);
  const previousFieldRequest = getPreviousRequest(
    triageFields,
    fieldRequests,
    field,
  );

  const goToNextStep = () => {
    if (nextFieldRequest) {
      navigate(
        `/direct/improve-application-questions/${nextFieldRequest.field}`,
      );
    }
  };

  const goToPreviousStep = () => {
    if (previousFieldRequest) {
      navigate(
        `/direct/improve-application-questions/${previousFieldRequest.field}`,
      );
    }
  };

  const redirectToPreview = () => {
    navigate("/direct/improve-application-questions/preview");
  };

  const onGoToNextStep = () => {
    delayAction(goToNextStep);
  };

  const onGoToPreviousStep = () => {
    delayAction(goToPreviousStep);
  };

  const goToPreview = () => {
    delayAction(redirectToPreview);
  };

  return (
    <section className="tw-flex tw-flex-col">
      {preview && (
        <h3 className="tw-order-none tw-pr-9 tw-text-[1.25rem]">{title}</h3>
      )}

      <div
        className={twMerge(
          "tw-relative tw-order-2 tw-w-full tw-rounded-lg tw-border-[1.5px] tw-border-solid tw-border-primary tw-px-6 tw-pb-6 tw-pt-8",
        )}
      >
        {fieldRequestSubtitle && (
          <Text size="sm" className="tw-mb-3 tw-font-bold">
            {fieldRequestSubtitle}
          </Text>
        )}
        {children}
      </div>

      {!preview && nextFieldRequest && (
        <FieldRequestNavigator
          menu
          className="tw-order-3"
          // next={nextFieldRequest && nextFieldRequest.displayName}
          onGoToNextStep={onGoToNextStep}
          onGoToPreviousStep={previousFieldRequest && onGoToPreviousStep}
          isCurrentFieldRequestSubmitted={isFieldRequestSubmitted}
          isCurrentFieldRequestSubmitting={Boolean(isFieldRequestSubmitting)}
          field={field}
        />
      )}

      {!preview && !nextFieldRequest && (
        <FieldRequestNavigator
          menu
          className="tw-order-3"
          goToPreview={goToPreview}
          onGoToPreviousStep={previousFieldRequest && onGoToPreviousStep}
          isCurrentFieldRequestSubmitted={isFieldRequestSubmitted}
          isCurrentFieldRequestSubmitting={Boolean(isFieldRequestSubmitting)}
          field={field}
        />
      )}

      {fieldRequestDescription && (
        <FieldRequestExplanation
          preview={preview}
          className={twMerge("tw-order-4", {
            "pr-9 tw-order-4": preview,
            "tw-mt-5 xl:tw-order-4 xl:tw-mt-9": !preview,
          })}
          title={t("iaq.whyImportantHeading")}
          text={fieldRequestDescription}
        />
      )}
    </section>
  );
};
